import { Box, Button } from "@vaporfi/uikit";
import { InfoIconMeme } from "@vaporfi/uikit/components/Icons";
import { Typography } from "@vaporfi/uikit/components/Typography";
import type React from "react";

export type RankInfoType =
	| "not-top-50"
	| "top-50"
	| "first-place"
	| "loading"
	| "wallet-not-connected"
	| "wrong-network";

interface UserRankInfoProps {
	type: RankInfoType;
	rank?: number;
	totalHolders?: number;
	tokenSymbol?: string;
	networkName?: string;
	onBuyClick?: () => void;
	onConnectWallet?: () => void;
	onSwitchNetwork?: () => void;
}

export const UserRankInfo: React.FC<UserRankInfoProps> = ({
	type,
	rank,
	totalHolders,
	tokenSymbol = "tokens",
	networkName = "APE",
	onBuyClick,
	onConnectWallet,
	onSwitchNetwork,
}) => {
	const getContent = () => {
		switch (type) {
			case "not-top-50":
				return {
					title: "You're not in the top 50 holders yet!",
					subtitle: `Buy at least some ${tokenSymbol} to climb the rank and start earning rewards!`,
					mobileSubtitle: `Buy ${tokenSymbol} to climb the rank and start earning rewards!`,
					buttonText: "Buy tokens",
					action: onBuyClick,
				};
			case "top-50":
				return {
					title: "Congratulations! You are in the top 50 holders!",
					subtitle: `Buy more ${tokenSymbol} to level up and earn even more rewards.`,
					mobileSubtitle: "Buy more to level up and earn rewards.",
					buttonText: "Buy tokens",
					action: onBuyClick,
				};
			case "first-place":
				return {
					title: "Congratulations! You are in first place!",
					subtitle: `Buy more ${tokenSymbol} to maintain your rank and maximize your rewards.`,
					mobileSubtitle: `Buy more ${tokenSymbol} to maintain your rank.`,
					buttonText: "Buy tokens",
					action: onBuyClick,
				};
			case "wallet-not-connected":
				return {
					title: "Wallet not connected!",
					subtitle: "Connect your wallet to see your information.",
					mobileSubtitle: "Connect your wallet to see your information.",
					buttonText: "Connect Wallet",
					action: onConnectWallet,
				};
			case "wrong-network":
				return {
					title: "Wrong network selected!",
					subtitle: `Switch to ${networkName} network to see your information.`,
					mobileSubtitle: `Switch to ${networkName} network.`,
					buttonText: "Switch Network",
					action: onSwitchNetwork,
				};
			case "loading":
				return {
					title: "Loading your rank information...",
					subtitle: "Please wait while we retrieve your position.",
					mobileSubtitle: "Please wait while we retrieve your position.",
					buttonText: "Buy tokens",
					action: onBuyClick,
				};
			default:
				return {
					title: "Connect your wallet to see your rank",
					subtitle: `Buy ${tokenSymbol} to earn rewards.`,
					mobileSubtitle: `Buy ${tokenSymbol} to earn rewards.`,
					buttonText: "Buy tokens",
					action: onBuyClick,
				};
		}
	};

	const content = getContent();

	const DesktopVersion = (
		<Box className="border border-turquoise-500 rounded-lg py-4 px-6 mb-6 flex items-start relative hidden md:flex">
			<Box className="text-white mr-4 pt-1">
				<InfoIconMeme width="24" height="24" />
			</Box>
			<Box className="flex-1 pr-32">
				<Typography weight="semibold" size="base">
					{content.title}
				</Typography>
				<Typography size="sm" color="neutral" className="mt-1">
					{type === "not-top-50" && rank && totalHolders && rank > 50
						? `You are rank ${rank} out of ${totalHolders} holders. ${content.subtitle}`
						: content.subtitle}
				</Typography>
			</Box>
			<Button
				color="blue"
				className="absolute right-6 top-1/2 transform -translate-y-1/2 rounded-lg px-4 py-2 hover:bg-blue-600 active:bg-blue-700 active:transform active:scale-[0.98] transition-all duration-150"
				onClick={content.action}
			>
				<Typography weight="semibold" size="base" color="light">
					{content.buttonText}
				</Typography>
			</Button>
		</Box>
	);

	const MobileVersion = (
		<Box className="border border-turquoise-500 rounded-lg p-4 mb-4 block md:hidden">
			<Box className="flex items-start mb-3">
				<Box className="text-white mr-3 flex-shrink-0">
					<InfoIconMeme width="24" height="24" />
				</Box>
				<Box>
					<Typography weight="semibold" size="base">
						{content.title}
					</Typography>
					<Typography size="xs" color="neutral" className="mt-1 text-gray-400">
						{type === "not-top-50" && rank && totalHolders && rank > 50
							? "Buy XXX,XXX.YY tokens to level up and earn even more rewards."
							: content.mobileSubtitle}
					</Typography>
				</Box>
			</Box>
			<Box className="flex justify-end">
				<Button
					color="blue"
					className="rounded-lg px-4 py-2"
					onClick={content.action}
				>
					<Typography weight="semibold" size="base" color="light">
						{content.buttonText}
					</Typography>
				</Button>
			</Box>
		</Box>
	);

	return (
		<>
			{DesktopVersion}
			{MobileVersion}
		</>
	);
};

export default UserRankInfo;
